import {
  isSessionOpen,
  type LiveViewSessionState,
  type LiveViewStatus
} from "./sessionState";

interface StageNotice {
  title: string;
  detail?: string;
  /** Still waiting on the other side: show the pulsing dot. */
  pending?: boolean;
  canAskAgain?: boolean;
  /**
   * The session is over: the viewer loses the screen at once, so the last
   * frame isn't left showing behind the notice (BSCPF-7033).
   */
  final?: boolean;
}

/**
 * Why a re-join was refused. "Ask again" sends a new `request`, which starts
 * a session of the viewer's own.
 */
const endedErrorDetail = (error: string) =>
  error === "not_session_viewer"
    ? "Someone else started this session. Ask again to start your own."
    : "The session is no longer available.";

export const firstNameOf = (name: string) =>
  name.trim().split(/\s+/)[0] || "The applicant";

/**
 * What to show over the phone screen for each session state; null while the
 * replay itself should be visible.
 */
export const getStageNotice = ({
  status,
  error,
  endedBy,
  connected,
  firstName
}: {
  status: LiveViewStatus;
  error?: string;
  endedBy?: LiveViewSessionState["endedBy"];
  connected: boolean;
  firstName: string;
}): StageNotice | null => {
  if (!connected && isSessionOpen(status))
    return { title: "Connection lost, reconnecting…", pending: true };

  switch (status) {
    case "requesting":
      return {
        title: `Asking ${firstName} to share their screen…`,
        pending: true
      };
    case "waiting":
      return {
        title: `Waiting for ${firstName} to accept…`,
        detail: "They'll see a request to share this application's screen.",
        pending: true
      };
    case "paused":
      return {
        title: `${firstName} left the app`,
        detail: "The view picks up again when they come back."
      };
    case "declined":
      return {
        title: `${firstName} declined`,
        detail: "They chose not to share their screen.",
        canAskAgain: true,
        final: true
      };
    case "expired":
      // One neutral line for both causes: they left, or the request timed out.
      return {
        title: `Request expired. ${firstName} didn't answer.`,
        canAskAgain: true,
        final: true
      };
    case "ended":
      return {
        title:
          endedBy === "applicant"
            ? `${firstName} stopped sharing`
            : "Live view ended",
        detail: error ? endedErrorDetail(error) : undefined,
        canAskAgain: true,
        final: true
      };
    case "failed":
      if (error === "connection_lost")
        return {
          title: "Connection lost",
          detail: "The live view stopped. Ask again once you're back online.",
          canAskAgain: true,
          final: true
        };
      return {
        title:
          error === "applicant_not_present"
            ? `${firstName} isn't in the application right now`
            : "Couldn't start live view",
        detail:
          error === "applicant_not_present"
            ? "You can watch while they're in the applicant app."
            : "Try again in a moment.",
        canAskAgain: true,
        final: true
      };
    default:
      return null;
  }
};
